import * as React from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import UndoIcon from '@mui/icons-material/Undo';
import MuiAppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { ColorRing } from 'react-loader-spinner';
import { useNavigation, useOutlet } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import { MainListItems, SecondaryListItems } from './ListItems';
import AdminDashboardView from 'UI/Application/Views/AdminDashboardView';
import { PRIVATE } from 'Shared/Routing/Application/Constants/PathsConstant';
import { useAuthContext } from 'Shared/Security/Domain/Contexts/authContext';

const drawerWidth = 240;

const copyrightEnabled = true;

function Copyright(props) {
  return (
    <Typography variant="body2" color="text.secondary" align="center" {...props}>
      {'Copyright © '}
      <Link color="inherit" href="/">
        {process.env.REACT_APP_SITENAME}
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
}

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen
      }),
      marginLeft: 0
    })
  }),
);

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open'
})(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    })
  })
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  height: '65px',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end'
}));

export default function Dashboard() {
  const [open, setOpen] = React.useState(true);
  const { state } = useNavigation();
  const { isImpersonating, revertImpersonation } = useAuthContext();

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const outlet = useOutlet();

  const loadOutlet = () => {
    if (state === "loading") {
      return (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <ColorRing
            visible={true}
            height="80"
            width="80"
            ariaLabel="blocks-loading"
            wrapperStyle={{}}
            wrapperClass="blocks-wrapper"
            colors={['#e15b64', '#f47e60', '#f8b26a', '#abbd81', '#849b87']}
          />
        </Box>
      );
    } else {
      if (outlet) {
        return outlet;
      } else {
        return <AdminDashboardView />;
      }
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={open} sx={{ height: '65px' }}>
        <Toolbar sx={{ height: '65px', display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{ mr: 2, ...(open && { display: 'none' }) }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 700 }}>
              {process.env.REACT_APP_SITENAME}
            </Typography>
            <Box
              sx={{
                ml: 2,
                px: 1.2,
                py: 0.3,
                bgcolor: 'rgba(255, 255, 255, 0.18)',
                borderRadius: '50px',
                fontSize: '0.72rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                display: { xs: 'none', sm: 'inline-block' },
              }}
            >
              Administración
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {isImpersonating && (
              <Button
                variant="contained"
                size="small"
                startIcon={<UndoIcon />}
                onClick={revertImpersonation}
                sx={{
                  bgcolor: '#f59e0b',
                  color: '#0f172a',
                  fontWeight: 800,
                  textTransform: 'none',
                  borderRadius: '50px',
                  px: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  '&:hover': {
                    bgcolor: '#d97706',
                    color: '#ffffff',
                  }
                }}
              >
                Volver usuario original
              </Button>
            )}
            <Button
              component={RouterLink}
              to={PRIVATE}
              color="inherit"
              variant="outlined"
              size="small"
              startIcon={<PersonOutlineIcon />}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.6)',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '50px',
                px: 2,
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.12)',
                },
              }}
            >
              Vista Usuario
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          height: '100vh',
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            overflowY: 'auto',
            overflowX: 'hidden',
            height: '100%',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '3px',
            },
          }
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <DrawerHeader sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
          <Typography variant="h6" noWrap sx={{ fontWeight: 800, color: '#257eb9', letterSpacing: 0.5, fontSize: '1.15rem' }}>
            {process.env.REACT_APP_SITENAME || 'ASZENDE'}
          </Typography>
        </DrawerHeader>
        <Divider />
        <List component="nav" sx={{ pb: 4 }}>
            <MainListItems/>
            <Divider sx={{ my: 1 }} />
            <SecondaryListItems />
        </List>
      </Drawer>
      <Main open={open} sx={{ minHeight: '100vh', padding: 0 }}>
        <DrawerHeader />
        <Box sx={{ margin: 0, padding: { xs: '16px', sm: '24px', md: '32px' }, minHeight: 'calc(100vh - 65px)', width: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>
            {loadOutlet()}
            {(() => {
              if (copyrightEnabled) {
                return (<Copyright sx={{ pt: 4, mt: 4 }}/>);
              }
            })()}
        </Box>
      </Main>
    </Box>
  );
}